import { firestore } from '../configs/firebase';
import {
  collection,
  getCountFromServer,
  query,
  where,
  getDocs,
  DocumentData,
} from 'firebase/firestore';
import { chunkArray } from '@leedpulse-monorepo/utils';
import { IUser } from '@leedpulse-monorepo/interfaces';
import { UserType } from '@leedpulse-monorepo/enums';

export const getHomeStats = async (userData: IUser, apiKey: string, collaboratorUid: string, scenarioId: string, period: [Date, Date]) => {
  try {
    const smsCollection = collection(firestore, 'SMS');
    const contactsCollection = collection(firestore, 'Contacts');

    const [startTime, endTime] = [period[0].valueOf(), period[1].valueOf()];
    const sliceMs = 24 * 60 * 60 * 1000;

    const commonConstraints = [
      where('id_api_key', '==', apiKey),
      ...(collaboratorUid
        ? [where('contact.owner', '==', collaboratorUid)]
        : []),
      ...(scenarioId
        ? [where('contact.prospect_category', '==', scenarioId)]
        : []),
    ];

    const buildContactQuery = (qualification: string) =>
      query(
        contactsCollection,
        ...commonConstraints,
        where('qualification_ai.timestamp', '>=', startTime),
        where('qualification_ai.timestamp', '<=', endTime),
        ...(qualification
          ? [where('qualification_ai.qualification', '==', qualification)]
          : [])
      );

    const buildActivityQuery = (repliedOnly = false, engaged: boolean) =>
      query(
        contactsCollection,
        ...commonConstraints,
        ...(engaged
          ? [where('last_activity.role', 'in', ['user', 'assistant'])]
          : []),
        ...(repliedOnly ? [where('has_replied', '==', true)] : [])
      );

    const fetchContactIds = async () => {
      const snap = await getDocs(
        query(contactsCollection, ...commonConstraints)
      );
      return snap.docs.map((doc) => doc.id);
    };

    const getSMSCounts = async (role: string, deliverability: string | null, contactIds: string[]) => {
      if (!contactIds.length) return 0;

      if (collaboratorUid || scenarioId) {
        const chunks = chunkArray(contactIds, 10);
        const results = await Promise.all(
          chunks.map((chunk) => {
            const constraints = [
              where('id_api_key', '==', apiKey),
              where('role', '==', role),
              where('created', '>=', startTime),
              where('created', '<=', endTime),
              where('id_contact', 'in', chunk),
              ...(deliverability
                ? [where('deliverability', '==', deliverability)]
                : []),
            ];
            return getDocs(query(smsCollection, ...constraints));
          })
        );
        return results.reduce((sum, snap) => sum + snap.size, 0);
      } else {
        const result = await getCountFromServer(
          query(
            smsCollection,
            where('id_api_key', '==', apiKey),
            where('role', '==', role),
            where('created', '>=', startTime),
            where('created', '<=', endTime),
            ...(deliverability
              ? [where('deliverability', '==', deliverability)]
              : [])
          )
        );
        return result.data().count;
      }
    };

    const aggregateSmsCountsByDay = async (contactIds: string[]) => {
      if (!contactIds.length)
        return { smsReceivedSliced: [], smsSentSliced: [] };

      const fetchByRole = async (role: string) => {
        if (collaboratorUid || scenarioId) {
          const chunks = chunkArray(contactIds, 10);
          const allDocs = await Promise.all(
            chunks.map((chunk) =>
              getDocs(
                query(
                  smsCollection,
                  where('id_api_key', '==', apiKey),
                  where('role', '==', role),
                  where('created', '>=', startTime),
                  where('created', '<=', endTime),
                  where('id_contact', 'in', chunk)
                )
              )
            )
          );
          return allDocs.flatMap((snap) =>
            snap.docs.map((doc) => doc.data())
          );
        } else {
          const result = await getDocs(
            query(
              smsCollection,
              where('id_api_key', '==', apiKey),
              where('role', '==', role),
              where('created', '>=', startTime),
              where('created', '<=', endTime)
            )
          );
          return result.docs.map((doc) => doc.data());
        }
      };

      const [smsUser, smsAssistant] = await Promise.all([
        fetchByRole('user'),
        fetchByRole('assistant'),
      ]);

      const sliceRange = (list: DocumentData[], t: number) =>
        list.filter((sms) => sms.created >= t && sms.created < t + sliceMs)
          .length;

      const smsReceivedSliced = [],
        smsSentSliced = [];

      for (let t = startTime; t < endTime; t += sliceMs) {
        smsReceivedSliced.push(sliceRange(smsUser, t));
        smsSentSliced.push(sliceRange(smsAssistant, t));
      }

      return { smsReceivedSliced, smsSentSliced };
    };

    const aggregateContactMetrics = async (engaged: boolean) => {
      const snap = await getDocs(buildActivityQuery(false, engaged));
      const contacts = snap.docs.map((doc) => doc.data());

      const sliceRange = (contacts: DocumentData[], t: number) =>
        contacts.filter((contact) => {
          if (!engaged) {
            return contact.created >= t && contact.created < t + sliceMs;
          } else {
            return (
              contact.history?.[0]?.type === 'messagesent' &&
              contact.history[0].timestamp >= t &&
              contact.history[0].timestamp < t + sliceMs
            );
          }
        }).length;

      const sliced = [];
      for (let t = startTime; t < endTime; t += sliceMs) {
        sliced.push(sliceRange(contacts, t));
      }
      return sliced;
    };

    const contactIds = await fetchContactIds();

    const [
      interestedSnap,
      consideringSnap,
      notInterestedSnap,
      notResponsiveSnap,
      notAvailableSnap,
      errorsCount,
      deliveredCount,
      tbdCount,
      smsReceivedPastPeriod,
      smsSentPastPeriod,
      contactsTotalSnap,
      contactsRespondersSnap,
    ] = await Promise.all([
      getCountFromServer(buildContactQuery('interested')),
      getCountFromServer(buildContactQuery('considering')),
      getCountFromServer(buildContactQuery('not_interested')),
      getCountFromServer(buildContactQuery('not_responsive')),
      getCountFromServer(buildContactQuery('not_available')),
      getSMSCounts('assistant', 'error', contactIds),
      getSMSCounts('assistant', 'delivered', contactIds),
      getSMSCounts('assistant', 'tbd', contactIds),
      getSMSCounts('user', null, contactIds),
      getSMSCounts('assistant', null, contactIds),
      getCountFromServer(buildActivityQuery(false, true)),
      getCountFromServer(buildActivityQuery(true, true)),
    ]);

    const nTotal = deliveredCount + errorsCount + tbdCount;
    const contactsTotalCount = contactsTotalSnap.data().count;
    const contactsRespondersCount = contactsRespondersSnap.data().count;

    const prcentResponders =
      contactsTotalCount > 0
        ? Math.round((contactsRespondersCount / contactsTotalCount) * 100)
        : 0;

    const prcentNotResponders = nTotal > 0 ? 100 - prcentResponders : 0;
    const prcentErrors = 0; // Tu peux ajuster ici si besoin.

    const [
      contactsEngagedSliced,
      contactsCreatedSliced,
      { smsReceivedSliced, smsSentSliced },
    ] = await Promise.all([
      aggregateContactMetrics(true),
      aggregateContactMetrics(false),
      userData?.type !== UserType.USER
        ? aggregateSmsCountsByDay(contactIds)
        : { smsReceivedSliced: [], smsSentSliced: [] },
    ]);

    return {
      success: true,
      status: 'success',
      msg: `Nombre de SMS Reçus : ${smsReceivedPastPeriod}; Envoyés : ${smsSentPastPeriod}`,
      doc: [
        { type: 'SMSReceivedPeriod', data: smsReceivedPastPeriod },
        { type: 'SMSSentPeriod', data: smsSentPastPeriod },
        { type: 'SMSReceivedPeriodSliced', data: smsReceivedSliced },
        { type: 'SMSSentPeriodSliced', data: smsSentSliced },
        {
          type: 'DeliverabilityStats',
          data: {
            prcentResponders,
            prcentNotResponders,
            prcentErrors,
          },
        },
        {
          type: 'ContactsStats',
          data: {
            interestedCount: interestedSnap.data().count,
            consideringCount: consideringSnap.data().count,
            notInterestedCount: notInterestedSnap.data().count,
            notResponsiveCount: notResponsiveSnap.data().count,
            notAvailableCount: notAvailableSnap.data().count,
          },
        },
        {
          type: 'ContactsEngagedStats',
          data: contactsEngagedSliced,
        },
        {
          type: 'ContactsCreatedStats',
          data: contactsCreatedSliced,
        },
      ],
    };
  } catch (error) {
    return {
      success: false,
      status: 'error',
      msg: (error as Error).message.toString(),
    };
  }
}

export default {
  getHomeStats,
};